'use client';

import React, { useState, useEffect, useRef } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Layers } from 'lucide-react';
import { useSession } from './SessionProvider';

const BANDS = [
  { key: 'delta', label: 'Delta', color: '#60a5fa', range: '0.5–4 Hz' },
  { key: 'theta', label: 'Theta', color: '#a78bfa', range: '4–8 Hz' },
  { key: 'alpha', label: 'Alpha', color: '#6ee7b7', range: '8–13 Hz' },
  { key: 'beta', label: 'Beta', color: '#fb923c', range: '13–30 Hz' },
];

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; color: string }>;
}

// Animated counter hook
function useAnimatedValue(target: number, duration = 450) {
  const [display, setDisplay] = useState(target);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);
  const fromRef = useRef(target);

  useEffect(() => {
    const from = fromRef.current;
    const to = target;
    if (Math.abs(to - from) < 0.05) return;

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    startRef.current = null;

    const animate = (ts: number) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(from + (to - from) * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);

  return display;
}

function BandValueCard({ band, value }: { band: typeof BANDS[0]; value: number }) {
  const [flash, setFlash] = useState(false);
  const prevRef = useRef(value);
  const animatedVal = useAnimatedValue(value);

  useEffect(() => {
    if (Math.abs(value - prevRef.current) > 0.3) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 600);
      prevRef.current = value;
      return () => clearTimeout(t);
    }
    prevRef.current = value;
  }, [value]);

  const isBeta = band.key === 'beta';

  return (
    <div
      className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-zinc-900/60 border border-zinc-800/50 relative overflow-hidden transition-all duration-300"
      style={{
        boxShadow: flash
          ? `0 0 18px 4px ${band.color}55, 0 0 0 1px ${band.color}40`
          : isBeta
          ? `0 0 12px rgba(249,115,22,0.15)`
          : `0 0 8px ${band.color}18`,
        borderColor: flash ? `${band.color}60` : undefined,
        transition: 'box-shadow 0.35s ease, border-color 0.35s ease',
      }}
    >
      {/* Pulsing glow overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at 50% 120%, ${band.color}18 0%, transparent 65%)`,
          animation: `bandPulse${band.key} 3.2s ease-in-out infinite`,
        }}
      />

      {/* Flash highlight */}
      {flash && (
        <div
          className="absolute inset-0 pointer-events-none rounded-lg"
          style={{
            background: `radial-gradient(ellipse at 50% 50%, ${band.color}28 0%, transparent 70%)`,
            animation: 'bandFlash 0.6s ease-out forwards',
          }}
        />
      )}

      <span
        className="w-2 h-2 rounded-full flex-shrink-0 relative z-10"
        style={{
          backgroundColor: band.color,
          boxShadow: `0 0 6px ${band.color}80`,
          animation: 'dotPulse 2.5s ease-in-out infinite',
        }}
      />
      <div className="min-w-0 relative z-10">
        <p className="text-[10px] text-zinc-500">
          {band.label} <span className="text-zinc-700">{band.range}</span>
        </p>
        <p
          className="text-[13px] font-mono font-semibold tabular-nums"
          style={{ color: band.color, transition: 'color 0.3s ease' }}
        >
          {animatedVal.toFixed(1)} <span className="text-[10px] text-zinc-600">µV²</span>
        </p>
      </div>

      <style jsx>{`
        @keyframes bandPulsedelta { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.9; } }
        @keyframes bandPulsetheta { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.9; } }
        @keyframes bandPulsealpha { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.9; } }
        @keyframes bandPulsebeta  { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }
        @keyframes bandFlash { 0% { opacity: 1; } 100% { opacity: 0; } }
        @keyframes dotPulse {
          0%, 100% { box-shadow: 0 0 4px ${band.color}60; }
          50% { box-shadow: 0 0 10px ${band.color}cc; }
        }
      `}</style>
    </div>
  );
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 shadow-2xl">
      <p className="text-[11px] text-zinc-500 mb-1.5 font-medium">Band Power (µV²)</p>
      <div className="space-y-1">
        {payload.map((entry) => {
          const band = BANDS.find((b) => b.key === entry.dataKey);
          return (
            <div key={`bp-tooltip-${entry.dataKey}`} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-[11px] text-zinc-400">{band?.label}</span>
                <span className="text-[10px] text-zinc-600">{band?.range}</span>
              </div>
              <span className="text-[11px] font-mono tabular-nums text-zinc-200">
                {entry.value.toFixed(1)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function BandPowerTimeline() {
  const { bandPowerHistory, metrics } = useSession();
  const chartData = bandPowerHistory.slice(-30).map((pt, i) => ({ ...pt, idx: i }));

  return (
    <div className="rounded-xl border border-zinc-800/60 bg-zinc-900/40 p-4 h-full flex flex-col">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-7 h-7 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center">
          <Layers size={14} className="text-violet-400" />
        </div>
        <div>
          <p className="text-[13px] font-semibold text-zinc-200">Band Power Timeline</p>
          <p className="text-[11px] text-zinc-600">Absolute power per frequency band</p>
        </div>
      </div>

      {/* Current band values — animated */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        {BANDS.map((band) => {
          const val = metrics[`${band.key}Power` as keyof typeof metrics] as number;
          return <BandValueCard key={`band-val-${band.key}`} band={band} value={val} />;
        })}
      </div>

      {/* Chart */}
      <div className="flex-1 min-h-[140px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
            <defs>
              {BANDS.map((band) => (
                <linearGradient key={`grad-${band.key}`} id={`grad-${band.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={band.color} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={band.color} stopOpacity={0.02} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.04)" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="idx" tick={false} axisLine={false} tickLine={false} />
            <YAxis
              domain={[0, 50]}
              tick={{ fontSize: 9, fill: '#52525b', fontFamily: 'IBM Plex Mono' }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            {BANDS.map((band) => (
              <Area
                key={`area-${band.key}`}
                type="monotone"
                dataKey={band.key}
                stroke={band.color}
                strokeWidth={1.5}
                fill={`url(#grad-${band.key})`}
                dot={false}
                isAnimationActive={false}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-2 flex-wrap">
        {BANDS.map((band) => (
          <div key={`bp-legend-${band.key}`} className="flex items-center gap-1.5">
            <span className="w-3 h-0.5 rounded-full" style={{ backgroundColor: band.color }} />
            <span className="text-[10px] font-mono text-zinc-500">{band.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}